import { ChangeEvent, DragEvent, FC, useState } from 'react';

import { Delete } from '@mui/icons-material';
import { Box, Typography, IconButton, Button } from '@mui/material';

export type FileUploadProps = {
  accept: string;
  hoverLabel?: string;
  dropLabel?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  image?: {
    url: string;
    imageStyle?: {
      width?: string;
      height?: string;
    };
  };
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
};

export const FileUpload: FC<FileUploadProps> = ({
  accept,
  hoverLabel = 'Click or drag to upload file',
  dropLabel = 'Drop file here',
  width = '600px',
  height = '140px',
  backgroundColor = '#fff',
  image: {
    url,
    imageStyle = {
      height: '100px',
    },
  } = {},
  onChange,
  onDrop,
}) => {
  const [imageUrl, setImageUrl] = useState(url);
  const [labelText, setLabelText] = useState(hoverLabel);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const stopDefaults = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const dragEvents = {
    onMouseEnter: () => {
      setIsMouseOver(true);
    },
    onMouseLeave: () => {
      setIsMouseOver(false);
    },
    onDragEnter: (e: DragEvent) => {
      stopDefaults(e);
      setIsDragOver(true);
      setLabelText(dropLabel);
    },
    onDragLeave: (e: DragEvent) => {
      stopDefaults(e);
      setIsDragOver(false);
      setLabelText(hoverLabel);
    },
    onDragOver: stopDefaults,
    onDrop: (e: DragEvent<HTMLElement>) => {
      stopDefaults(e);
      setLabelText(hoverLabel);
      setIsDragOver(false);
      if (e.dataTransfer.files[0]) {
        setImageUrl(URL.createObjectURL(e.dataTransfer.files[0]));
      }
      onDrop(e);
    },
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files instanceof FileList) {
      setImageUrl(URL.createObjectURL(event?.target?.files[0]));
    }

    onChange(event);
  };

  return (
    <>
      <input
        onChange={handleChange}
        accept={accept}
        hidden
        id='file-upload'
        type='file'
      />

      <label htmlFor='file-upload' {...dragEvents}>
        <Box
          width={width}
          height={height}
          bgcolor={backgroundColor}
          sx={{
            backgroundColor: '#F0F0F0',
            // flexDirection: 'column',
            borderRadius: 2,
            border: 'medium dashed gray',
          }}
        >
          <Box
            height={height}
            width={width}
            sx={{
              pointerEvents: 'none',
              fontWeight: isDragOver || isMouseOver ? 'bold' : 'normal',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }} variant='h5'>
              {labelText}
            </Typography>
            {url ? (
              <Box>
                <IconButton
                  sx={{ pointerEvents: 'all', justifyContent: 'right' }}
                  onClick={() => setImageUrl(undefined)}
                >
                  <Delete />
                </IconButton>
                <img alt='upload file' src={imageUrl} style={imageStyle} />
              </Box>
            ) : (
              <>
                <Typography variant='h5'>or</Typography>
                <Button variant='outlined'>Upload File</Button>
              </>
            )}
          </Box>
        </Box>
      </label>
    </>
  );
};
